import Ajv from 'ajv';
import addFormats from 'ajv-formats';
import adapt from './adapt';
import unflatList from './unflatList';
import flatList from './flatList';
import mappedRequired from './mappedRequired';
import { execute } from './sandboxCode';

export const errorMessages = {
    required: 'cfm_label_required_field',
    minLength: 'cfm_label_error_minlength',
    maxLength: 'cfm_label_error_maxlength',
    type: 'cfm_label_error_type',
    maximum: 'cfm_error_max_num',
    minimum: 'cfm_error_min_num',
    exclusiveMaximum: 'cfm_label_error_exclusivemaximum',
    exclusiveMinimum: 'cfm_label_error_exclusiveminimum',
    maxItems: 'cfm_label_error_maxitems',
    minItems: 'cfm_label_error_minitems',
};

function isValidCustomError(error) {
    if (!error || typeof error !== 'object') return false;
    return !Object.keys(error).find(
        (key) =>
            typeof error[key] !== 'object' ||
            (typeof error[key] === 'object' && !error[key].message),
    );
}

function executeCustomValidations(validations, data, errors, schema) {
    return new Promise((resolve, reject) => {
        const validationPrs = validations
            .filter((validation) => validation.event === 'onSubmit')
            .map((validation) => execute(validation.code, { data, errors, schema }));
        if (validationPrs && validationPrs.length) {
            Promise.all(validationPrs)
                .then((errors) => {
                    let finalErrors = errors.reduce((obj, error) => {
                        if (isValidCustomError(error)) obj = { ...obj, ...error };
                        return obj;
                    }, {});
                    resolve(finalErrors);
                })
                .catch((error) => {
                    reject({
                        message: 'Custom validations failed',
                        error,
                    });
                });
        } else resolve({});
    });
}

function getVisibleFields(composition, visibleFields) {
    return composition.reduce((vf, el) => {
        if (el.composition?.length && !el.isHidden)
            return getVisibleFields(el.composition, [...vf, el.schemaId]);
        if (!el.isHidden) return [...vf, el.schemaId];
        return vf;
    }, visibleFields);
}
const adaptWithUndefineds = (data) => {
    return Object.keys(data).reduce((newData, key) => {
        if (data[key] === null || data[key] === '') newData[key] = undefined;
        else newData[key] = data[key];

        return newData;
    }, {});
};

function flatSchema(composition, sortedErrorFields) {
    return composition.reduce((arr, el) => {
        if (el.composition?.length)
            return [...arr, ...flatSchema(el.composition, sortedErrorFields)];
        return [...arr, el.schemaId || el.id];
    }, sortedErrorFields);
}

export default (data, schema, isBulk, noUnflat) => {
    return new Promise((resolve) => {
        let errors = {};

        const ajv = new Ajv({
            verbose: true,
            removeAdditional: true,
            useDefaults: true,
            coerceTypes: true,
            allErrors: true,
            strict: false,
        });

        addFormats(ajv);

        // We should apply required validation to empty strings too

        let cleanData = adapt(data, schema);

        cleanData = flatList(cleanData, schema);

        const visibleFields = getVisibleFields(schema.layout.composition, []);
        const bulkEdited = Object.keys(data);
        const cleanSchema = { ...schema };
        cleanSchema.required = [
            ...(schema?.required?.filter(
                (el) => visibleFields?.includes(el) && (!isBulk || bulkEdited.includes(el)),
            ) || []),
            ...mappedRequired(schema.layout),
        ];

        const finalDataUndefineds = adaptWithUndefineds(cleanData);
        const isValid = ajv.validate(cleanSchema, finalDataUndefineds);

        if (!isValid) {
            ajv.errors.forEach((error) => {
                const errorKeyword = error.keyword;
                let key = error.schemaPath.split('/')[2];
                if (!key) key = error.params.missingProperty;
                errors[key] = {
                    message: errorMessages[errorKeyword] || errorKeyword,
                    params: Array.isArray(error.schema) ? error.schema : [error.schema],
                    error,
                };
            });
        }

        if (!noUnflat) cleanData = unflatList(data, cleanData, schema);

        const sortedErrorFields = flatSchema(schema.layout.composition, []).filter(
            (el) => errors[el],
        );

        document
            .querySelector(`.fs-form__field__${sortedErrorFields[0]}`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });

        let result = { isValid, errors, cleanData };
        if (schema?.context?.object?.validations) {
            executeCustomValidations(
                schema?.context?.object?.validations,
                cleanData,
                errors,
                schema,
            )
                .then((errors) => {
                    result.errors = { ...result.errors, ...errors };
                    resolve(result);
                })
                .catch((err) => {
                    console.error('Execute custom validations', err);
                    resolve(result);
                });
        } else resolve(result);
    });
};
